import React, { useEffect, useState } from 'react';
import { Bell, BellOff, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useLang } from '../contexts/LangContext';
import { useMode } from '../contexts/ModeContext';
import { pushAPI } from '../utils/api';

// Convert urlBase64-encoded VAPID public key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

const STORAGE_KEY = 'hafizim_daily_reminder';

const DailyReminder = () => {
  const { lang } = useLang();
  const { isKidsMode } = useMode();
  const [permission, setPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
  const [enabled, setEnabled] = useState(false);
  const [hour, setHour] = useState(20);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setEnabled(!!parsed.enabled);
        setHour(parsed.hour || 20);
      }
    } catch (_) { /* ignore */ }
  }, []);

  // Persist settings
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ enabled, hour })); } catch (_) { /* ignore */ }
  }, [enabled, hour]);

  // Schedule check every minute (in-page only — not background)
  useEffect(() => {
    if (!enabled || permission !== 'granted') return;
    const id = setInterval(() => {
      const now = new Date();
      const lastSentKey = `${STORAGE_KEY}_last_${now.toISOString().slice(0, 10)}`;
      if (localStorage.getItem(lastSentKey)) return;
      if (now.getHours() === hour && now.getMinutes() < 5) {
        try {
          new Notification(
            lang === 'tr' ? 'Hafızım — Günlük Hatırlatma 🌙' : 'Hafızım — Daily Reminder 🌙',
            {
              body: lang === 'tr' ? 'Bugün biraz Kuran okuma vakti! Kaldığın yerden devam et.' : 'Time for a little Quran today. Continue where you left off.',
              icon: '/favicon.ico',
              tag: 'hafizim-daily',
            }
          );
          localStorage.setItem(lastSentKey, '1');
        } catch (_) { /* ignore */ }
      }
    }, 60_000);
    return () => clearInterval(id);
  }, [enabled, permission, hour, lang]);

  const askPermission = async () => {
    if (typeof Notification === 'undefined') {
      toast.error(lang === 'tr' ? 'Tarayıcınız bildirim desteklemiyor' : 'Browser does not support notifications');
      return;
    }
    try {
      const res = await Notification.requestPermission();
      setPermission(res);
      if (res === 'granted') {
        setEnabled(true);
        // Register service worker + VAPID push subscription
        try {
          if ('serviceWorker' in navigator && 'PushManager' in window) {
            const reg = await navigator.serviceWorker.register('/service-worker.js');
            // Get VAPID public key
            const keyRes = await pushAPI.getPublicKey();
            const publicKey = keyRes.data?.key;
            if (publicKey) {
              const existing = await reg.pushManager.getSubscription();
              const subscription = existing || await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey),
              });
              await pushAPI.subscribe(subscription.toJSON()).catch(() => {});
            }
            // Periodic background sync as additional fallback (Chrome only)
            if ('periodicSync' in reg) {
              try {
                const status = await navigator.permissions.query({ name: 'periodic-background-sync' });
                if (status.state === 'granted' || status.state === 'prompt') {
                  await reg.periodicSync.register('hafizim-daily-reminder', { minInterval: 12 * 60 * 60 * 1000 });
                }
              } catch (_) { /* ignore */ }
            }
          }
        } catch (_) { /* ignore */ }
        toast.success(lang === 'tr' ? 'Bildirim açıldı! Günlük hatırlatma alacaksın.' : 'Notifications enabled!');
      } else {
        toast.info(lang === 'tr' ? 'İzin reddedildi' : 'Permission denied');
      }
    } catch (_) {
      toast.error('Hata');
    }
  };

  const toggleEnabled = () => {
    if (!enabled && permission !== 'granted') {
      askPermission();
      return;
    }
    setEnabled((v) => !v);
    toast.success(!enabled
      ? (lang === 'tr' ? 'Günlük hatırlatma açıldı' : 'Daily reminder on')
      : (lang === 'tr' ? 'Günlük hatırlatma kapatıldı' : 'Daily reminder off')
    );
  };

  const sendTestNotification = async () => {
    if (permission !== 'granted') {
      askPermission();
      return;
    }
    try {
      // Try VAPID-based server push first
      const r = await pushAPI.test().catch(() => null);
      if (r?.data?.sent > 0) {
        toast.success(lang === 'tr' ? 'Sunucu push gönderildi 🔔' : 'Server push sent 🔔');
        return;
      }
      // Fallback to client SW message
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'SHOW_REMINDER', lang });
        return;
      }
      new Notification(
        lang === 'tr' ? 'Test Bildirimi 🔔' : 'Test Notification 🔔',
        { body: lang === 'tr' ? 'Bildirimler çalışıyor!' : 'Notifications are working!', icon: '/favicon.ico' }
      );
    } catch (_) {
      toast.error('Gönderilemedi');
    }
  };

  const accent = isKidsMode ? 'amber' : 'teal';
  const isOn = enabled && permission === 'granted';

  return (
    <div className={`rounded-2xl border p-5 ${isOn ? (isKidsMode ? 'bg-amber-50/60 border-amber-200' : 'bg-teal-50/60 border-teal-200') : 'bg-white border-stone-200'}`} data-testid="daily-reminder">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl ${isOn ? (isKidsMode ? 'bg-amber-500 text-white' : 'bg-teal-700 text-white') : 'bg-stone-100 text-stone-500'} flex items-center justify-center`}>
            {isOn ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
          </div>
          <div>
            <h3 className="font-bold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {lang === 'tr' ? 'Günlük Hatırlatma' : 'Daily Reminder'}
            </h3>
            <p className="text-xs text-stone-500">
              {permission === 'unsupported'
                ? (lang === 'tr' ? 'Bu tarayıcı desteklemiyor' : 'Browser unsupported')
                : isOn
                  ? (lang === 'tr' ? `Her gün ${String(hour).padStart(2, '0')}:00'da bildirim alacaksın` : `Daily ping at ${String(hour).padStart(2, '0')}:00`)
                  : (lang === 'tr' ? 'Her gün Kuran okuman için sana hatırlatalım' : 'We\'ll remind you daily')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isOn && (
            <select
              value={hour}
              onChange={(e) => setHour(parseInt(e.target.value, 10))}
              data-testid="reminder-hour"
              className="text-xs px-2 py-1.5 rounded-lg border border-stone-200 bg-white"
            >
              {Array.from({ length: 24 }, (_, i) => i).map((h) => (
                <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
              ))}
            </select>
          )}
          {permission === 'granted' && isOn && (
            <button
              type="button"
              onClick={sendTestNotification}
              data-testid="reminder-test"
              className="text-xs px-3 py-1.5 rounded-lg border border-stone-200 bg-white text-stone-700 hover:border-stone-400"
            >
              {lang === 'tr' ? 'Test' : 'Test'}
            </button>
          )}
          <button
            type="button"
            onClick={toggleEnabled}
            disabled={permission === 'unsupported'}
            data-testid="reminder-toggle"
            className={`text-xs font-semibold px-4 py-1.5 rounded-lg text-white ${isOn ? (isKidsMode ? 'bg-amber-500 hover:bg-amber-600' : 'bg-teal-700 hover:bg-teal-800') : 'bg-stone-600 hover:bg-stone-700'} disabled:opacity-50`}
          >
            {isOn ? (lang === 'tr' ? 'Açık ✓' : 'On ✓') : (lang === 'tr' ? 'Aç' : 'Enable')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DailyReminder;
